import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  FormControl,
  FormLabel,
  HStack,
  Stack,
} from "@chakra-ui/react";
import YearSelect from "@raiden/library/components/Form/YearSelect";
import {
  DOCUMENTS_TYPES_VALUE_CUSTOMER,
  DOCUMENTS_TYPES_VALUE_HOLDER,
} from "@raiden/library/constants/documents";
import {
  HOLDERS_STATES_VALUE_ENABLED,
  HOLDERS_STATES_VALUE_PENDING,
} from "@raiden/library/constants/holders";
import useAuthAs from "@raiden/library/hooks/auth/as";
import { useApi } from "@raiden/library/hooks/useApi";
import { usePreferredYear } from "@raiden/library/hooks/usePreferredYear";
import generateApiUri from "@raiden/library/libraries/utils/generateApiUri";
import { useState } from "react";
import { useIntl } from "react-intl";
import SWRHandler from "../../components/SWR/SWRHandler";
import DocumentsData from "./DocumentsData";

/**
 * @typedef {Object} Props
 * @property {boolean} [withYearSelector]
 *
 * @param {Props} props
 */
function DocumentsHandler({ withYearSelector = true }) {
  const intl = useIntl();

  const { user } = useAuthAs();

  const preferredYear = usePreferredYear({
    environmentId: user?.environment_id,
  });

  const [yearId, setYearId] = useState(
    preferredYear?.id === undefined ? null : String(preferredYear.id),
  );

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Holder").Holder[]>} */
  const { swrResponse: swrResponseHolders } = useApi(
    user?.id !== undefined && yearId !== null
      ? generateApiUri({
          id: "@holders.viewAny",
          query: {
            state: [HOLDERS_STATES_VALUE_ENABLED, HOLDERS_STATES_VALUE_PENDING],
          },
        })
      : null,
  );

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Document").Document[]>} */
  const { swrResponse: swrResponseDocuments } = useApi(
    yearId !== null
      ? generateApiUri({
          id: "@documents.viewAny",
          query: {
            type: [
              DOCUMENTS_TYPES_VALUE_CUSTOMER,
              DOCUMENTS_TYPES_VALUE_HOLDER,
            ],
            year_id: [yearId],
            fields: ["document.policies"],
          },
        })
      : null,
  );

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/CustomerDocument").CustomerDocument[]>} */
  const { swrResponse: swrResponseCustomerDocuments } = useApi(
    user?.id !== undefined
      ? generateApiUri({
          id: "@customers.documents.search",
          query: {
            holder_id: [],
            year_id: [yearId],
            fields: [
              "user.customer.document.document",
              "user.customer.document.pages",
              "user.customer.document.page.policies",
              "user.customer.document.policies",
            ],
          },
        })
      : null,
    {
      swrConfig: {
        revalidateOnFocus: true,
      },
    },
  );

  return (
    <>
      <Alert status="info">
        <AlertIcon />

        <AlertDescription>
          {intl.formatMessage({
            defaultMessage:
              "Merci de nous fournir vos documents justificatifs pour pouvoir profiter de nos activités.",
          })}
        </AlertDescription>
      </Alert>

      {yearId === null ? (
        <Alert status="warning" mt="1rem">
          <AlertIcon />

          <Box>
            <AlertTitle>
              {intl.formatMessage({
                defaultMessage: "Aucun document ne peut être affiché",
              })}
            </AlertTitle>

            <AlertDescription>
              {intl.formatMessage({
                defaultMessage:
                  "Aucune période n'a été définie sur cet environnement.",
              })}
            </AlertDescription>
          </Box>
        </Alert>
      ) : (
        <Stack spacing="1rem">
          {withYearSelector && (
            <HStack mt="1rem">
              <FormControl width="auto">
                <FormLabel>
                  {intl.formatMessage({ defaultMessage: "Année scolaire" })}
                </FormLabel>

                <YearSelect
                  value={yearId}
                  onChange={(e) => setYearId(e.target.value)}
                  environmentId={String(user?.environment_id)}
                  allowClear={false}
                />
              </FormControl>
            </HStack>
          )}

          {user && (
            <SWRHandler
              swrResponse={swrResponseHolders}
              shouldRenderWithEmptyData={true}
              keepDataOnRevalidation={true}>
              {(holdersResponse) => (
                <SWRHandler
                  swrResponse={swrResponseDocuments}
                  keepDataOnRevalidation={true}>
                  {(documentsResponse) => (
                    <SWRHandler
                      swrResponse={swrResponseCustomerDocuments}
                      shouldRenderWithEmptyData={true}
                      keepDataOnRevalidation={true}>
                      {(customerDocumentsResponse) => (
                        <DocumentsData
                          customerId={user.id}
                          mutateCustomerDocuments={
                            swrResponseCustomerDocuments.mutate
                          }
                          documents={documentsResponse.data}
                          customerDocuments={customerDocumentsResponse.data}
                          holders={holdersResponse.data}
                        />
                      )}
                    </SWRHandler>
                  )}
                </SWRHandler>
              )}
            </SWRHandler>
          )}
        </Stack>
      )}
    </>
  );
}

export default DocumentsHandler;
